import React, { useEffect, useRef, useState } from 'react';
import { startAttendanceCamera } from '../../web/attendanceCamera';
import { getFaceAttendanceConfig } from '../config/faceAttendanceConfig';
import type { AttendanceCapture } from '../types/faceAttendance';

type Props = {
  heading?: string;
  onCaptured: (capture: AttendanceCapture) => void;
  onError: (message: string) => void;
  onTimeout?: () => void;
  noFaceTimeoutSec?: number;
};

/** Webpack resolves this file only on Web. Native camera/hooks stay untouched. */
export function KioskFaceCapture({
  heading = 'Office kiosk',
  onCaptured,
  onError,
  noFaceTimeoutSec,
}: Props) {
  const video = useRef<Parameters<typeof startAttendanceCamera>[0]>(null);
  const callbacks = useRef({ onCaptured, onError });
  callbacks.current = { onCaptured, onError };
  const [status, setStatus] = useState('Preparing camera…');

  useEffect(() => {
    if (!video.current) return;
    try {
      const config = getFaceAttendanceConfig();
      return startAttendanceCamera(video.current, {
        imageMaxWidth: config.imageMaxWidth,
        imageMaxHeight: config.imageMaxHeight,
        captureQuality: config.captureQuality,
        timeoutMs:
          noFaceTimeoutSec == null
            ? config.resultTimeoutMs
            : noFaceTimeoutSec * 1000,
        onStatus: setStatus,
        onCaptured: (capture: AttendanceCapture) =>
          callbacks.current.onCaptured(capture),
        onError: (message: string) => callbacks.current.onError(message),
      });
    } catch (error) {
      callbacks.current.onError(
        error instanceof Error ? error.message : 'Unable to open the camera.',
      );
    }
  }, [noFaceTimeoutSec]);

  return React.createElement(
    'section',
    {
      'aria-label': 'Face attendance camera',
      style: {
        display: 'flex',
        flex: 1,
        minHeight: 0,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 24,
        color: 'white',
        background: '#0B1220',
      },
    },
    React.createElement('h2', { style: { margin: 0 } }, heading),
    React.createElement(
      'p',
      { style: { margin: 0 } },
      'Center your face in the camera',
    ),
    React.createElement('video', {
      ref: video,
      autoPlay: true,
      muted: true,
      playsInline: true,
      'aria-label': 'Front camera preview',
      style: {
        width: 'min(80vw, 480px)',
        maxHeight: '60vh',
        aspectRatio: '3 / 4',
        objectFit: 'cover',
        borderRadius: '50%',
        border: '3px solid #5BA3FF',
        transform: 'scaleX(-1)',
        background: '#060A12',
      },
    }),
    React.createElement(
      'p',
      {
        role: 'status',
        'aria-live': 'polite',
        style: { margin: 0, textAlign: 'center' },
      },
      status,
    ),
  );
}
